import React, { useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import Field from '../components/Field/Field'
import TeamPalette from '../components/TeamPalette/TeamPalette'
import AnimationEditor from '../components/AnimationEditor/AnimationEditor'
import PlayControls from '../components/PlayControls/PlayControls'
import Timeline from '../components/Timeline/Timeline'
import PlayManager from '../components/PlayManager/PlayManager'
import NavigationHeader from '../components/NavigationHeader'
import AICoach from '../components/PracticePlanner/AICoach'
import usePlayStore from '../stores/playStore'
import { nanoid } from 'nanoid'

const DashboardPage = () => {
  const [searchParams] = useSearchParams()
  const { 
    currentPlay, 
    setCurrentPlay, 
    loadPlay, 
    teams, 
    props, 
    animations, 
    clearTeams, 
    clearProps, 
    clearAnimations,
    addTeam,
    addProp,
    addAnimation,
    updateFieldSettings,
    setDuration
  } = usePlayStore()

  // Mock API call - generates play based on prompt
  const mockGeneratePlay = useCallback(async (userPrompt) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    // Parse the prompt to extract information
    const lowerPrompt = userPrompt.toLowerCase()
    
    // Determine number of players per team (default 5v5)
    let playersPerTeam = 5
    const vMatch = userPrompt.match(/(\d+)\s*v\s*(\d+)/i) || userPrompt.match(/(\d+)\s*vs?\s*(\d+)/i)
    if (vMatch) {
      playersPerTeam = Math.min(parseInt(vMatch[1]), parseInt(vMatch[2]), 11) // Cap at 11
    } else {
      // Try single number like "4 players" or "4v4"
      const singleMatch = userPrompt.match(/(\d+)\s*(?:player|v)/i)
      if (singleMatch) {
        playersPerTeam = Math.min(parseInt(singleMatch[1]), 11)
      }
    }
    
    // Determine field type
    let fieldType = 'full'
    if (lowerPrompt.includes('half field') || lowerPrompt.includes('half-field') || lowerPrompt.includes('half')) {
      fieldType = 'half'
    } else if (lowerPrompt.includes('plain')) {
      fieldType = 'plain'
    }
    
    // Determine play type
    const isCornerKick = lowerPrompt.includes('corner') || lowerPrompt.includes('corner kick')
    const isFreeKick = lowerPrompt.includes('free kick') || lowerPrompt.includes('freekick')
    const isThrowIn = lowerPrompt.includes('throw') || lowerPrompt.includes('throw-in')
    const isKickoff = lowerPrompt.includes('kickoff') || lowerPrompt.includes('kick-off')
    const isPenalty = lowerPrompt.includes('penalty')
    const isAttacking = lowerPrompt.includes('attack') || lowerPrompt.includes('offensive')
    const isDefensive = lowerPrompt.includes('defense') || lowerPrompt.includes('defensive')
    
    // Generate play name
    const playName = isCornerKick ? 'Corner Kick Play' :
                     isFreeKick ? 'Free Kick Play' :
                     isThrowIn ? 'Throw-In Play' :
                     isKickoff ? 'Kickoff Play' :
                     isPenalty ? 'Penalty Play' :
                     isAttacking ? 'Attacking Play' :
                     isDefensive ? 'Defensive Play' :
                     `${playersPerTeam}v${playersPerTeam} Play`
    
    // Field dimensions (approximate - will be adjusted by Phaser scene)
    // Full field: ~1000x667, Half field: ~500x667
    const fieldWidth = fieldType === 'half' ? 500 : 1000
    const fieldHeight = 667
    const fieldCenterX = fieldWidth / 2
    const fieldCenterY = fieldHeight / 2
    
    // Minimum spacing between players to avoid overlap (in pixels)
    const minSpacing = 60
    
    // Generate teams
    const generatedTeams = []
    const timestamp = Date.now()
    
    // Helper function to check if a position is too close to existing positions
    const isPositionValid = (x, y, existingPositions, minDistance) => {
      return !existingPositions.some(pos => {
        const distance = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2))
        return distance < minDistance
      })
    }
    
    // Helper function to generate non-overlapping positions in a formation
    const generateFormation = (centerX, centerY, count, sideOffset, fieldType) => {
      const positions = []
      const availableWidth = fieldType === 'half' ? 200 : 400
      const availableHeight = fieldHeight * 0.6 // Use 60% of field height
      
      // Try different formation patterns based on player count
      if (count <= 3) {
        // Small groups: triangle or line
        const spacing = Math.max(minSpacing, availableHeight / (count + 1))
        for (let i = 0; i < count; i++) {
          const y = fieldCenterY - (availableHeight / 2) + (i + 1) * spacing
          positions.push({ x: centerX + sideOffset, y: y })
        }
      } else if (count <= 5) {
        // Medium groups: 2-3 rows
        const rows = count <= 3 ? 2 : 3
        const cols = Math.ceil(count / rows)
        const rowSpacing = Math.max(minSpacing, availableHeight / (rows + 1))
        const colSpacing = Math.max(minSpacing, availableWidth / (cols + 1))
        
        for (let i = 0; i < count; i++) {
          const row = Math.floor(i / cols)
          const col = i % cols
          const x = centerX + sideOffset - (availableWidth / 2) + (col + 1) * colSpacing
          const y = fieldCenterY - (availableHeight / 2) + (row + 1) * rowSpacing
          positions.push({ x, y })
        }
      } else {
        // Larger groups: grid formation with more spacing
        const rows = Math.ceil(Math.sqrt(count))
        const cols = Math.ceil(count / rows)
        const rowSpacing = Math.max(minSpacing, availableHeight / (rows + 1))
        const colSpacing = Math.max(minSpacing, availableWidth / (cols + 1))
        
        for (let i = 0; i < count; i++) {
          const row = Math.floor(i / cols)
          const col = i % cols
          const x = centerX + sideOffset - (availableWidth / 2) + (col + 1) * colSpacing
          const y = fieldCenterY - (availableHeight / 2) + (row + 1) * rowSpacing
          positions.push({ x, y })
        }
      }
      
      return positions
    }
    
    // Red team (attacking/left side)
    const redSideOffset = fieldType === 'half' ? -150 : -300
    const redPositions = generateFormation(fieldCenterX, fieldCenterY, playersPerTeam, redSideOffset, fieldType)
    redPositions.forEach((pos, i) => {
      generatedTeams.push({
        id: `${timestamp}-red-${i}`,
        type: 'red-team',
        number: (i + 1).toString(),
        x: pos.x,
        y: pos.y,
        color: '#EF4444'
      })
    })
    
    // Blue team (defending/right side)
    const blueSideOffset = fieldType === 'half' ? 150 : 300
    const bluePositions = generateFormation(fieldCenterX, fieldCenterY, playersPerTeam, blueSideOffset, fieldType)
    bluePositions.forEach((pos, i) => {
      generatedTeams.push({
        id: `${timestamp}-blue-${i}`,
        type: 'blue-team',
        number: (i + 1).toString(),
        x: pos.x,
        y: pos.y,
        color: '#3B82F6'
      })
    })
    
    // Generate soccer ball - start with first red team player (with small offset to avoid overlap)
    const firstPlayer = generatedTeams[0]
    const ballOffset = 15 // Small offset so ball doesn't overlap with player sprite
    const ballStartX = firstPlayer.x + ballOffset
    const ballStartY = firstPlayer.y + ballOffset
    const ballId = `${timestamp}-ball`
    
    const generatedProps = [{
      id: ballId,
      type: 'soccer-ball',
      x: ballStartX,
      y: ballStartY,
      color: '#000000'
    }]
    
    // Generate animations - ball passing sequence
    const generatedAnimations = []
    const animationDuration = 2 // seconds per pass
    let currentTime = 0
    
    // Create a passing sequence: ball moves through first 3-4 red team players
    const redTeamPlayers = generatedTeams.filter(t => t.type === 'red-team')
    const passingSequence = redTeamPlayers.slice(0, Math.min(4, playersPerTeam))
    
    for (let i = 0; i < passingSequence.length - 1; i++) {
      const fromPlayer = passingSequence[i]
      const toPlayer = passingSequence[i + 1]
      
      // Create path points for smooth ball movement
      const pathPoints = []
      const steps = 10
      for (let step = 0; step <= steps; step++) {
        const progress = step / steps
        const x = fromPlayer.x + (toPlayer.x - fromPlayer.x) * progress
        const y = fromPlayer.y + (toPlayer.y - fromPlayer.y) * progress
        pathPoints.push({
          x: x,
          y: y,
          time: progress * animationDuration
        })
      }
      
      generatedAnimations.push({
        id: `${timestamp}-ball-anim-${i}`,
        propId: ballId,
        type: 'move',
        startTime: currentTime,
        endTime: currentTime + animationDuration,
        startX: fromPlayer.x,
        startY: fromPlayer.y,
        endX: toPlayer.x,
        endY: toPlayer.y,
        path: pathPoints,
        duration: animationDuration
      })
      
      currentTime += animationDuration
    }
    
    // Update duration to match animations
    const totalDuration = currentTime
    
    return {
      id: nanoid(8),
      name: playName,
      description: `AI-generated ${playName.toLowerCase()} based on your request: "${userPrompt}"`,
      teams: generatedTeams,
      props: generatedProps,
      animations: generatedAnimations,
      fieldSettings: {
        showGrid: true,
        showNumbers: true,
        zoom: 1,
        panX: 0,
        panY: 0,
        fieldType: fieldType
      },
      duration: totalDuration
    }
  }, [])

  // ProtectedRoute already handles authentication verification
  // No need to check again here

  // Load play from query parameter if present
  useEffect(() => {
    const loadPlayId = searchParams.get('load')
    if (loadPlayId) {
      loadPlay(loadPlayId).catch(error => {
        console.error('Error loading play:', error)
        alert('Failed to load play: ' + error.message)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-screen w-screen flex flex-col bg-gray-50">
        {/* Navigation Header */}
        <NavigationHeader />

        {/* Page Actions Toolbar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <PlayManager />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex min-h-0">
          {/* Left Sidebar - Field Elements (Team Palette) */}
          <aside className="w-64 bg-white shadow-sm border-r border-gray-200 p-4 overflow-y-auto">
            <TeamPalette />
          </aside>
          
          {/* Animation Editor */}
          <aside className="w-80 bg-white shadow-sm border-r border-gray-200 p-4 overflow-y-auto">
            <AnimationEditor />
          </aside>

          {/* Center - Field */}
          <main className="flex-1 flex flex-col min-h-0 border-r border-gray-200">
            <div className="flex-1 relative min-h-0" style={{ minHeight: '50vh' }}>
              <Field />
            </div>
            
            {/* Bottom Controls */}
            <div className="bg-white border-t border-gray-200 flex flex-col" style={{ height: '250px' }}>
              <div className="flex items-center justify-between p-4 pb-2 flex-shrink-0">
                <PlayControls />
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                <Timeline />
              </div>
            </div>
          </main>
          
          {/* Right Sidebar - AI Coach */}
          <aside className="w-80 bg-white shadow-sm overflow-hidden">
            <AICoach
              mode="play"
              title="AI Coach"
              description="Describe the play you need, and AI will design it for you."
              labelText="Play Request"
              buttonText="Generate Play"
              placeholder="e.g., Create a corner kick play with players in the box"
              examplePrompts={[
                '• "Corner kick play with players in the box"',
                '• "Free kick play from the left side"',
                '• "Attacking play with overlapping runs"',
                '• "Defensive set piece organization"'
              ]}
              onGenerate={mockGeneratePlay}
              getExistingItems={() => {
                // Check if there are teams, props, or animations
                return (teams?.length > 0 || props?.length > 0 || animations?.length > 0) ? [1] : []
              }}
              clearItems={() => {
                clearTeams()
                clearProps()
                clearAnimations()
              }}
              setItems={(generatedPlay) => {
                // Clear existing items first
                clearTeams()
                clearProps()
                clearAnimations()
                
                // Add generated teams (store will generate new IDs)
                generatedPlay.teams.forEach((team) => {
                  addTeam({
                    type: team.type,
                    x: team.x,
                    y: team.y,
                    color: team.color,
                    number: team.number
                  })
                })
                
                // Add generated props (store will generate new IDs, but we'll try to preserve)
                generatedPlay.props.forEach((prop) => {
                  addProp({
                    type: prop.type,
                    x: prop.x,
                    y: prop.y,
                    color: prop.color
                  })
                })
                
                // Use setTimeout to allow store updates to complete, then map IDs and add animations
                setTimeout(() => {
                  const storeState = usePlayStore.getState()
                  const addedTeams = storeState.teams
                  const addedProps = storeState.props
                  
                  // Create ID mapping for teams (match by type, number, and position)
                  const teamIdMap = new Map()
                  generatedPlay.teams.forEach((originalTeam) => {
                    const matchingTeam = addedTeams.find(t => 
                      t.type === originalTeam.type && 
                      t.number === originalTeam.number &&
                      Math.abs(t.x - originalTeam.x) < 20 &&
                      Math.abs(t.y - originalTeam.y) < 20
                    )
                    if (matchingTeam) {
                      teamIdMap.set(originalTeam.id, matchingTeam.id)
                    }
                  })
                  
                  // Create ID mapping for props (match by type and position)
                  const propIdMap = new Map()
                  generatedPlay.props.forEach((originalProp) => {
                    const matchingProp = addedProps.find(p => 
                      p.type === originalProp.type &&
                      Math.abs(p.x - originalProp.x) < 20 &&
                      Math.abs(p.y - originalProp.y) < 20
                    )
                    if (matchingProp) {
                      propIdMap.set(originalProp.id, matchingProp.id)
                    }
                  })
                  
                  // Add generated animations with corrected IDs
                  generatedPlay.animations.forEach(animation => {
                    const updatedAnimation = { ...animation }
                    
                    // Update teamId if present
                    if (animation.teamId) {
                      const newTeamId = teamIdMap.get(animation.teamId)
                      if (newTeamId) {
                        updatedAnimation.teamId = newTeamId
                      } else {
                        delete updatedAnimation.teamId
                      }
                    }
                    
                    // Update propId if present
                    if (animation.propId) {
                      const newPropId = propIdMap.get(animation.propId)
                      if (newPropId) {
                        updatedAnimation.propId = newPropId
                      } else {
                        delete updatedAnimation.propId
                      }
                    }
                    
                    addAnimation(updatedAnimation)
                  })
                  
                  // Update field settings
                  if (generatedPlay.fieldSettings) {
                    updateFieldSettings(generatedPlay.fieldSettings)
                  }
                  
                  // Update duration if provided
                  if (generatedPlay.duration) {
                    setDuration(generatedPlay.duration)
                  }
                  
                  // Update current play metadata
                  setCurrentPlay({
                    ...currentPlay,
                    name: generatedPlay.name,
                    description: generatedPlay.description,
                    id: generatedPlay.id
                  })
                }, 100) // Small delay to ensure store updates complete
              }}
              confirmationMessage="This will replace all existing teams, props, and animations with the new AI-generated play. Are you sure you want to continue?"
            />
          </aside>
        </div>
      </div>
    </DndProvider>
  )
}

export default DashboardPage

